import { useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, Mail, Rocket, Users, XCircle } from 'lucide-react'

import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs } from '@/components/ui/tabs'
import { ApplicationStatusBadge, VacancyStatusBadge, applicationLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import {
  useAdvanceApplication,
  useCloseVacancy,
  usePublishVacancy,
  useVacancy,
  useVacancyApplications,
  useVacancyReadiness,
} from '@/features/hiring/api'
import { ApiError } from '@/lib/api'
import { formatDateTime, formatRelative, formatRupiah } from '@/lib/format'
import type { ApplicationState, JobApplication } from '@/lib/types'

const route = getRouteApi('/authenticated/app/perusahaan/lowongan/$vacancyId')

const stages: Array<{ value: ApplicationState | 'all'; label: string }> = [
  { value: 'all', label: 'Semua' },
  { value: 'submitted', label: 'Baru masuk' },
  { value: 'reviewed', label: 'Ditinjau' },
  { value: 'shortlisted', label: 'Daftar pendek' },
  { value: 'interview', label: 'Wawancara' },
  { value: 'offered', label: 'Ditawari' },
  { value: 'hired', label: 'Diterima' },
  { value: 'rejected', label: 'Tidak lanjut' },
]

export function VacancyPipelinePage() {
  const { vacancyId } = route.useParams()
  const { data: vacancyData, isLoading } = useVacancy(vacancyId)
  const [stage, setStage] = useState<ApplicationState | 'all'>('all')
  const { data: applicationData } = useVacancyApplications(vacancyId, stage === 'all' ? undefined : stage)
  const { data: readiness } = useVacancyReadiness(vacancyId, vacancyData?.vacancy.status === 'draft')

  const publish = usePublishVacancy(vacancyId)
  const closeVacancy = useCloseVacancy(vacancyId)
  const toast = useToast()

  const [advanceTarget, setAdvanceTarget] = useState<{ application: JobApplication; state: ApplicationState } | null>(
    null,
  )

  if (isLoading) return <Skeleton className="h-96 rounded-2xl" />

  if (!vacancyData) {
    return (
      <Card>
        <EmptyState
          title="Lowongan tidak ditemukan"
          description="Lowongan mungkin sudah dihapus."
          action={
            <Link to="/app/perusahaan">
              <Button variant="outline">Kembali</Button>
            </Link>
          }
        />
      </Card>
    )
  }

  const vacancy = vacancyData.vacancy
  const applications = applicationData?.applications ?? []
  const pipeline = applicationData?.pipeline ?? {}

  return (
    <div className="space-y-6">
      <Link
        to="/app/perusahaan"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Perekrutan
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <VacancyStatusBadge status={vacancy.status} />
            <span className="text-sm text-muted">{vacancy.company.name}</span>
          </div>
          <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-ink">{vacancy.title}</h1>
          <p className="mt-1 text-sm text-muted">
            {vacancy.application_count} pelamar · {vacancy.view_count} kali dilihat
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {vacancy.status === 'open' ? (
            <>
              <Link to="/lowongan/$vacancyId" params={{ vacancyId: vacancy.id }}>
                <Button variant="outline">
                  <ExternalLink className="size-4" />
                  Halaman publik
                </Button>
              </Link>
              <Button
                variant="outline"
                loading={closeVacancy.isPending}
                onClick={() =>
                  closeVacancy.mutate(true, {
                    onSuccess: () => toast.info('Lowongan ditandai sudah terisi'),
                  })
                }
              >
                Tandai sudah terisi
              </Button>
            </>
          ) : vacancy.status === 'draft' ? (
            <Button
              loading={publish.isPending}
              disabled={readiness ? !readiness.ready : false}
              onClick={() =>
                publish.mutate(undefined, {
                  onSuccess: () => toast.success('Lowongan tayang', 'Pelamar sudah dapat mengirim lamaran.'),
                  onError: (error) =>
                    toast.error('Gagal menayangkan', error instanceof ApiError ? error.message : undefined),
                })
              }
            >
              <Rocket className="size-4" />
              Tayangkan lowongan
            </Button>
          ) : null}
        </div>
      </header>

      {vacancy.status === 'draft' && readiness && !readiness.ready ? (
        <Card className="border-warning/40 bg-warning-soft">
          <CardBody>
            <p className="font-bold text-ink">Lengkapi sebelum tayang</p>
            <ul className="mt-2 space-y-1 text-sm text-muted">
              {readiness.reasons.map((reason) => (
                <li key={reason}>• {reason}</li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-4">
        {(['submitted', 'shortlisted', 'interview', 'hired'] as ApplicationState[]).map((state) => (
          <Card key={state}>
            <CardBody>
              <p className="text-sm text-muted">{applicationLabel(state)}</p>
              <p className="mt-1 text-2xl font-extrabold text-ink">{pipeline[state] ?? 0}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <Tabs
        value={stage}
        onChange={setStage}
        items={stages.map((item) => ({
          value: item.value,
          label: item.label,
          count: item.value === 'all' ? undefined : pipeline[item.value],
        }))}
      />

      {applications.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users className="size-6" />}
            title="Belum ada pelamar pada tahap ini"
            description="Pelamar baru akan muncul di sini beserta surat lamaran dan ekspektasi gajinya."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {applications.map((application) => (
            <Card key={application.id}>
              <CardBody className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex min-w-0 gap-3">
                    <Avatar name={application.candidate.full_name} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-bold text-ink">{application.candidate.full_name}</p>
                        <ApplicationStatusBadge state={application.state} />
                      </div>
                      {application.candidate.headline ? (
                        <p className="text-sm text-muted">{application.candidate.headline}</p>
                      ) : null}
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-subtle">
                        <span className="inline-flex items-center gap-1">
                          <Mail className="size-3.5" />
                          {application.candidate.email}
                        </span>
                        <span>Melamar {formatRelative(application.applied_at)}</span>
                      </p>
                    </div>
                  </div>

                  {application.expected_salary ? (
                    <div className="text-right">
                      <p className="text-xs text-subtle">Ekspektasi gaji</p>
                      <p className="font-bold tabular-nums text-ink">
                        {formatRupiah(application.expected_salary)}
                      </p>
                    </div>
                  ) : null}
                </div>

                <p className="whitespace-pre-line rounded-xl bg-sunken p-4 text-sm leading-relaxed text-muted">
                  {application.cover_letter}
                </p>

                <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
                  <Link
                    to="/talenta/$expertId"
                    params={{ expertId: application.candidate.id }}
                    className="text-sm font-semibold text-brand hover:underline"
                  >
                    Lihat profil talenta
                  </Link>

                  <span className="flex-1" />

                  {application.next_states
                    .filter((state) => state !== 'rejected')
                    .map((state) => (
                      <Button
                        key={state}
                        size="sm"
                        variant={state === 'hired' ? 'success' : 'secondary'}
                        onClick={() => setAdvanceTarget({ application, state })}
                      >
                        {applicationLabel(state)}
                      </Button>
                    ))}

                  {application.next_states.includes('rejected') ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:bg-danger-soft"
                      onClick={() => setAdvanceTarget({ application, state: 'rejected' })}
                    >
                      <XCircle className="size-4" />
                      Tidak lanjut
                    </Button>
                  ) : null}
                </div>

                {application.events && application.events.length > 0 ? (
                  <p className="text-xs text-subtle">
                    Terakhir diperbarui {formatDateTime(application.updated_at)}
                  </p>
                ) : null}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <AdvanceDialog vacancyId={vacancyId} target={advanceTarget} onClose={() => setAdvanceTarget(null)} />
    </div>
  )
}

function AdvanceDialog({
  vacancyId,
  target,
  onClose,
}: {
  vacancyId: string
  target: { application: JobApplication; state: ApplicationState } | null
  onClose: () => void
}) {
  const advance = useAdvanceApplication(vacancyId)
  const toast = useToast()
  const [note, setNote] = useState('')

  const rejecting = target?.state === 'rejected'

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      size="sm"
      title={target ? `Pindahkan ke ${applicationLabel(target.state)}` : ''}
      description={target?.application.candidate.full_name}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant={rejecting ? 'danger' : 'primary'}
            loading={advance.isPending}
            onClick={() => {
              if (!target) return
              advance.mutate(
                { applicationId: target.application.id, state: target.state, note },
                {
                  onSuccess: () => {
                    toast.success('Tahap diperbarui', 'Kandidat menerima pemberitahuan.')
                    setNote('')
                    onClose()
                  },
                  onError: (error) =>
                    toast.error('Gagal memperbarui tahap', error instanceof ApiError ? error.message : undefined),
                },
              )
            }}
          >
            {rejecting ? 'Kirim keputusan' : 'Pindahkan tahap'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Textarea
          label="Catatan untuk kandidat"
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={
            rejecting
              ? 'Sampaikan alasannya secara ringkas dan jujur. Kandidat berhak tahu duduk perkaranya.'
              : 'Misalnya jadwal wawancara, tautan pertemuan, atau berkas yang perlu disiapkan.'
          }
        />
        <p className="text-xs leading-relaxed text-subtle">
          Catatan ini tampil pada halaman lamaran kandidat dan tercatat pada riwayat yang tidak dapat diubah.
        </p>
        {target ? <Badge tone="neutral">Tahap saat ini: {applicationLabel(target.application.state)}</Badge> : null}
      </div>
    </Dialog>
  )
}
